/**
 * The estate if the household simply stays where it is (owner, 9 Oct 2026).
 *
 * The advice document's situation page shows today's position at home — pension, cashflow and net
 * worth — and the fourth tile, "What's left to the family", was the IN-CARE do-nothing figure: a
 * different basis from the other three, and for a village resident it carried the unit's payout
 * banked in year one. So the tile now projects the household as it is, with no move into care,
 * over the same horizon; the in-care baseline bridges to the strategies in a sentence under it.
 *
 * Null where someone is already in residential care (there is no "staying where you are" without
 * care to project), where the horizon is nil, or where the run fails — a missing figure is said as
 * missing, never as nothing.
 */
import { estateOf } from '../../../../shared/src/calculation/support/estateCgt.ts';
import * as calculationApi from '../../services/calculationApi.ts';
import { NO_CARE_COSTS } from '../../utils/doNothingBaseline.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

/** A care start the run never reaches: the household stays in its recorded arrangement. */
const NEVER = '2199-01-01';

export function anyoneInCareToday(ff: DraftFactFind | null | undefined): boolean {
    const client = (ff as { client?: { currentResidency?: string } } | null | undefined)?.client?.currentResidency;
    const partner = (ff as { partner?: { currentResidency?: string } } | null | undefined)?.partner?.currentResidency;
    return client === 'aged_care' || ((ff as { adviceFor?: string } | null | undefined)?.adviceFor === 'couple' && partner === 'aged_care');
}

export async function stayPutEstate(
    factFind: DraftFactFind | null | undefined,
    legacyYears: number,
    opts: { projectionPeriodMode?: string } = {},
): Promise<number | null> {
    if (!factFind || !(legacyYears > 0) || anyoneInCareToday(factFind)) return null;
    const living = parseFloat(String((factFind as { expenses?: { livingExpenses?: unknown } }).expenses?.livingExpenses ?? '')) || 0;
    try {
        const rows = (await calculationApi.runProjections(
            factFind,
            {
                rentHome: false,
                downsizeHome: false,
                careStartDate: NEVER,
                scenarioLivingExpenses: living,
                ...(opts.projectionPeriodMode ? { projectionPeriodMode: opts.projectionPeriodMode } : {}),
            } as never,
            { ...NO_CARE_COSTS, scenarioLivingExpenses: living } as never,
            legacyYears,
        )) as Array<{ netWorth?: unknown }> | null | undefined;
        const last = rows?.[legacyYears - 1] ?? rows?.[(rows?.length ?? 0) - 1];
        return estateOf(last as never);
    } catch {
        return null;
    }
}
